import React from "react";
import { api, ASSET_URL } from "../../lib/api";
import { X, Upload, Search, Film } from "lucide-react";
import { toast } from "sonner";

// Reusable media picker from the media library with quick upload.
// filter="image" => images/posters, filter="video" => MP4/WebM videos.
export default function MediaPickerModal({ onSelect, onClose, filter = "image" }) {
  const [items, setItems] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [q, setQ] = React.useState("");
  const fileRef = React.useRef();

  const load = React.useCallback(() => {
    return api.adminMedia().then(d => setItems((d || []).filter(m => (filter ? m.media_type === filter : true))));
  }, [filter]);

  React.useEffect(() => { load(); }, [load]);

  const term = q.trim().toLowerCase();
  const shown = term
    ? items.filter(m => [m.original_filename, m.alt_de, m.alt_en, m.title, m.media_type].some(v => (v || "").toLowerCase().includes(term)))
    : items;

  const accept = filter === "video"
    ? "video/mp4,video/webm,.mp4,.webm"
    : filter === "document"
      ? "application/pdf,.pdf"
      : "image/jpeg,image/png,image/webp,image/*";

  const uploadLabel = filter === "video"
    ? "Video hochladen"
    : filter === "document"
      ? "PDF hochladen"
      : "Bild hochladen";

  const emptyLabel = filter === "video"
    ? "Keine Videos"
    : filter === "document"
      ? "Keine Dokumente"
      : "Keine Bilder";

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const a = await api.uploadMedia(f, filter || "image");
      await load();
      toast.success(`${filter === "video" ? "Video" : "Datei"} hochgeladen`);
      onSelect(a);
    } catch {
      toast.error("Upload fehlgeschlagen");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div className="fixed inset-0 z-[210] flex items-center justify-center bg-black/70 p-4" data-testid="media-picker-modal" onClick={onClose}>
      <div className="bg-[var(--bg)] border-2 border-[var(--ink)] w-full max-w-3xl h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b-2 border-[var(--ink)] shrink-0">
          <div className="font-mono text-xs uppercase tracking-[0.2em]">{filter === "video" ? "Video auswählen" : "Medium auswählen"}</div>
          <div className="flex items-center gap-3">
            <label className="brut-btn brut-btn-ghost !py-1.5 !px-3 text-xs cursor-pointer">
              <Upload size={12} /> {busy ? "Lädt…" : uploadLabel}
              <input ref={fileRef} type="file" accept={accept} className="hidden" onChange={upload} />
            </label>
            <button onClick={onClose} data-testid="media-picker-close"><X size={18} /></button>
          </div>
        </div>

        <div className="p-3 border-b-2 border-[var(--ink)] shrink-0">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Suchen nach Dateiname oder Alt-Text…"
              className="w-full pl-9 pr-3 py-2 bg-transparent border-2 border-[var(--ink)] rounded-md text-sm focus:outline-none focus:border-[var(--accent)]"
              data-testid="media-picker-search"
            />
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="p-4 grid grid-cols-3 sm:grid-cols-4 gap-3">
            {shown.map(m => (
              <button key={m.id} onClick={() => onSelect(m)} className="group flex flex-col border-2 border-[var(--line-soft)] hover:border-[var(--ink)] transition-colors text-left" data-testid={`media-pick-${m.id}`} title={m.original_filename || m.media_type}>
                <div className="aspect-square w-full overflow-hidden bg-[var(--line-soft)] flex items-center justify-center shrink-0 relative">
                  {m.media_type === "video" && m.variants?.original ? (
                    <>
                      <video src={ASSET_URL(m.variants.original)} muted playsInline preload="metadata" className="w-full h-full object-cover bg-black" />
                      <span className="absolute bottom-1 right-1 bg-[var(--ink)] text-[var(--bg)] rounded-full p-1"><Film size={12} /></span>
                    </>
                  ) : (m.variants?.thumb || m.variants?.webp) ? (
                    <img src={ASSET_URL(m.variants.thumb || m.variants.webp)} alt={m.alt_de} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <span className="font-mono text-[11px] uppercase opacity-60 px-2 text-center">{m.media_type}</span>
                  )}
                </div>
                <div className="px-2 py-1.5 border-t-2 border-[var(--line-soft)] w-full bg-[var(--bg)]">
                  <div className="font-mono text-[10px] leading-snug break-all" style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{m.original_filename || m.title || "—"}</div>
                </div>
              </button>
            ))}
            {shown.length === 0 && <div className="col-span-full text-center font-mono text-xs opacity-50 py-8">{term ? "Keine Treffer" : emptyLabel}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
